/**
 * nonlinTemp.ts — Non-linear temperature gradient stresses in a two-layer slab
 *
 * Port of Nonlin_temp.xls (A. Gotlif / L. Khazanovich). A temperature profile
 * through the surface (PCC) layer is split into constant + linear + non-linear
 * parts; the non-linear residual — plus the curling restraint contributed by the
 * base layer — is locked in as self-equilibrating stress at every depth.
 *
 * Verified bit-for-bit against the workbook (LibreOffice recalc) on linear and
 * quadratic profiles, both bond conditions. See nonlinear_temp_stress.md.
 *
 * Units are US customary and pass through unchanged:
 *   E1, E2 [psi] · h1, h2 [in] · alpha [1/°F] · temperatures [°F] · stress [psi]
 * Sign convention for stress: tension positive, compression negative.
 */

export const N_NODES = 11; // fixed by the Δh/6 quadrature (10 slices)

export type Bond = 'bonded' | 'unbonded';

export interface NonlinTempInputs {
  /** 11 node temperatures, surface (T[0]) → bottom of slab (T[10]), °F. */
  T: number[];
  E1: number;    // surface-layer modulus, psi
  mu: number;    // Poisson's ratio
  E2: number;    // base-layer modulus, psi
  alpha: number; // coefficient of thermal expansion, 1/°F
  h1: number;    // surface-layer thickness, in
  h2: number;    // base-layer thickness, in
  bond: Bond;
}

export interface NonlinTempResult {
  x: number;        // neutral-axis depth from surface, in
  heff: number;     // effective thickness, in
  Dteff: number;    // equivalent linear temperature difference, °F
  Tc: number;       // constant (membrane) temperature component, °F
  TlinTop: number;  // linear component at surface, °F
  TlinBot: number;  // linear component at bottom of slab, °F
  TnlTop: number;   // non-linear residual at surface, °F
  TnlBot: number;   // non-linear residual at bottom of slab, °F
  signTop: number;  // stress at surface, psi (tension +)
  signBot: number;  // stress at bottom of slab, psi (tension +)
  /** Through-depth profiles at the 11 nodes (surface → bottom). */
  depth: number[];  // in, 0 … h1
  Tlin: number[];   // linear temperature component (relative to bottom), °F
  Tnl: number[];    // non-linear residual, °F
  sigma: number[];  // self-equilibrating stress, psi (tension +)
  sigmaMax: number; // max |sigma| over the depth, psi
}

/** Trapezoidal mean temperature of the surface layer. */
function trapezoidMean(T: number[]): number {
  let s = 0.5 * T[0] + 0.5 * T[10];
  for (let i = 1; i <= 9; i++) s += T[i];
  return s / 10;
}

export function computeNonlinTemp(inp: NonlinTempInputs): NonlinTempResult {
  const { T, E1, mu, E2, alpha, h1, h2, bond } = inp;
  const n = E2 / E1;
  const dh = h1 / 10;

  // Zeroth temperature moment, referenced to the bottom fiber.
  const S0 = (trapezoidMean(T) - T[10]) * h1;

  // Neutral axis (x) and effective thickness (heff) — the only geometry that
  // differs between bond conditions.
  let x: number, heff: number;
  if (bond === 'bonded') {
    x = (0.5 * h1 ** 2 + n * h2 * (h1 + h2 / 2)) / (h1 + n * h2);
    heff = Math.cbrt(
      h1 ** 3 + n * h2 ** 3 +
      12 * (h1 * (x - h1 / 2) ** 2 + n * h2 * (h1 - x + h2 / 2) ** 2),
    );
  } else {
    x = 0.5 * h1;
    heff = Math.cbrt(h1 ** 3 + n * h2 ** 3);
  }

  // First temperature moment (Simpson-like Δh/6 quadrature over 10 slices).
  let sum = 0;
  for (let i = 1; i <= 10; i++) {
    const k1 = (3 * i - 2) * dh - 3 * x;
    sum += T[i - 1] * k1 + T[i] * (k1 + dh);
  }
  const S1n = (sum * dh) / 6 - 0.5 * T[10] * h1 * (h1 - 2 * x);

  // Equivalent linear ΔT, constant + linear temperature components.
  let Dteff: number, Tc: number, TlinBot: number, TlinTop: number;
  if (bond === 'bonded') {
    const bracket =
      h1 ** 3 + 3 * h1 * x ** 2 - 3 * h1 ** 2 * x +
      n * (h2 ** 3 + 3 * (h1 - x) ** 2 * h2 + 3 * (h1 - x) * h2 ** 2);
    Dteff = ((-48 * S1n) / heff ** 2) * (bracket / heff ** 3);
    Tc = S0 / (h1 + n * h2);
    TlinBot = (12 * (h1 - x) * S1n) / heff ** 3;
    TlinTop = (-12 * x * S1n) / heff ** 3;
  } else {
    Dteff = (-12 * S1n) / heff ** 2;
    Tc = S0 / h1;
    TlinBot = ((12 * S1n) / heff ** 3) * (h1 / 2);
    TlinTop = ((-12 * S1n) / heff ** 3) * (h1 / 2);
  }

  const factor = (-E1 * alpha) / (1 - mu);
  const TnlBot = -Tc - TlinBot;
  const TnlTop = (T[0] - T[10]) - Tc - TlinTop;

  // Through-depth profiles: linear component interpolated between surface and
  // bottom; non-linear residual = (measured − constant − linear); stress = k·Tnl.
  const depth: number[] = [];
  const Tlin: number[] = [];
  const Tnl: number[] = [];
  const sigma: number[] = [];
  let sigmaMax = 0;
  for (let j = 0; j <= 10; j++) {
    const z = j * dh;
    const tl = TlinTop + (TlinBot - TlinTop) * (z / h1);
    const tnl = (T[j] - T[10]) - Tc - tl;
    const s = factor * tnl;
    depth.push(z);
    Tlin.push(tl);
    Tnl.push(tnl);
    sigma.push(s);
    if (Math.abs(s) > sigmaMax) sigmaMax = Math.abs(s);
  }

  return {
    x, heff, Dteff, Tc, TlinTop, TlinBot, TnlTop, TnlBot,
    signTop: factor * TnlTop,
    signBot: factor * TnlBot,
    depth, Tlin, Tnl, sigma, sigmaMax,
  };
}

/** Convenience profile generators for the UI (all °F, surface → bottom). */
export function linearProfile(tTop: number, tBot: number): number[] {
  return Array.from({ length: N_NODES }, (_, j) => tTop + (tBot - tTop) * (j / 10));
}

/**
 * Curved gradient with a nonlinearity exponent. exp = 1 → linear; exp > 1
 * concentrates the difference near the surface (typical daytime positive
 * gradient); exp < 1 near the bottom. tTop/tBot are the surface/bottom temps.
 */
export function curvedProfile(tTop: number, tBot: number, exp: number): number[] {
  return Array.from({ length: N_NODES }, (_, j) => {
    const frac = j / 10; // 0 at surface, 1 at bottom
    return tBot + (tTop - tBot) * Math.pow(1 - frac, exp);
  });
}
